import { Check, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    dismissVoiceCallCallerReview,
    linkVoiceCallCallerReview,
    useOrganizationClientSearch,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { useInvalidateCallerReviewQueries } from '@apps/web/swr_data_fetching/use_caller_review_queries_refresh';
import type { VoiceCallCallerReviewActionRead } from '@libraries/typescript/api_client/schema_types';

type ReviewResolution = Pick<
    VoiceCallCallerReviewActionRead,
    'remembered_for_future_calls' | 'remembering_unavailable_reason'
>;

export function callerReviewResolutionMessage(
    resolution: ReviewResolution,
): string {
    if (resolution.remembered_for_future_calls) {
        return 'Call linked. Future calls from this number will be recognized.';
    }
    if (resolution.remembering_unavailable_reason === 'shared_phone_number') {
        return 'Call linked. This shared number will still require review on future calls.';
    }
    return 'Call linked for this call only. Future calls from this number will still require review.';
}

function errorMessage(error: unknown): string {
    return error instanceof Error
        ? error.message
        : 'Something went wrong. Refresh and try again.';
}

export function VoiceCallCallerReviewDialog({
    callerPhone,
    initialContactName,
    onOpenChange,
    onResolved,
    open,
    organizationId,
    voiceCallId,
}: {
    readonly callerPhone: string;
    readonly initialContactName?: string | null;
    readonly onOpenChange: (open: boolean) => void;
    readonly onResolved: (resolution: ReviewResolution) => void;
    readonly open: boolean;
    readonly organizationId: string;
    readonly voiceCallId: string;
}) {
    const [comboboxOpen, setComboboxOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 300);
    const clientSearch = debouncedSearch.length >= 3 ? debouncedSearch : '';
    const { data: clients = [], isLoading: clientsLoading } =
        useOrganizationClientSearch(
            open && comboboxOpen ? organizationId : null,
            { reason: 'voice-call-caller-review' },
            { search: clientSearch, limit: 8 },
        );
    const invalidate = useInvalidateCallerReviewQueries(organizationId);
    const [selectedClient, setSelectedClient] = useState<{
        readonly id: string;
        readonly name: string;
    } | null>(null);
    const [recognizeFutureCalls, setRecognizeFutureCalls] = useState(true);
    const [contactName, setContactName] = useState(initialContactName ?? '');
    const [relation, setRelation] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            setComboboxOpen(false);
            setSearch('');
            setSelectedClient(null);
            setRecognizeFutureCalls(true);
            setContactName(initialContactName ?? '');
            setRelation('');
            setError(null);
        }
    }, [initialContactName, open]);

    const contactNameMissing =
        recognizeFutureCalls && contactName.trim().length === 0;
    const canSubmit =
        selectedClient !== null && !contactNameMissing && !isSubmitting;

    async function submit() {
        if (!canSubmit || selectedClient === null) {
            setError(
                selectedClient === null
                    ? 'Select a client.'
                    : 'Enter the caller or family contact name.',
            );
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            const resolution = await linkVoiceCallCallerReview(
                organizationId,
                voiceCallId,
                {
                    client_id: selectedClient.id,
                    contact_name: recognizeFutureCalls
                        ? contactName.trim()
                        : null,
                    relation: recognizeFutureCalls
                        ? relation.trim() || null
                        : null,
                    remember_for_future_calls: recognizeFutureCalls,
                },
            );
            toast.success(callerReviewResolutionMessage(resolution), {
                persistNotification: false,
            });
            onResolved(resolution);
            onOpenChange(false);
            await invalidate();
        } catch (caught) {
            setError(errorMessage(caught));
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Link unknown caller to a client</DialogTitle>
                    <DialogDescription>
                        Review {callerPhone} and choose the client this call
                        belongs to.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label>Client</Label>
                        <Combobox
                            open={comboboxOpen}
                            onOpenChange={setComboboxOpen}
                            displayValue={selectedClient?.name}
                            placeholder={
                                clientsLoading
                                    ? 'Searching clients…'
                                    : 'Search for a client'
                            }
                            disabled={isSubmitting}
                            triggerProps={{ 'aria-label': 'Client' }}
                            searchValue={search}
                            onSearchValueChange={setSearch}
                            searchPlaceholder="Search clients…"
                        >
                            <CommandEmpty>
                                {clientsLoading
                                    ? 'Loading clients…'
                                    : 'No active clients found.'}
                            </CommandEmpty>
                            {clients.map((client) => (
                                <CommandItem
                                    key={client.id}
                                    value={`${client.name} ${client.id}`}
                                    onSelect={() => {
                                        setSelectedClient(client);
                                        setComboboxOpen(false);
                                        setError(null);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4',
                                            selectedClient?.id === client.id
                                                ? 'opacity-100'
                                                : 'opacity-0',
                                        )}
                                    />
                                    {client.name}
                                </CommandItem>
                            ))}
                        </Combobox>
                    </div>
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="recognize-future-caller"
                            checked={recognizeFutureCalls}
                            disabled={isSubmitting}
                            onCheckedChange={(checked) =>
                                setRecognizeFutureCalls(checked === true)
                            }
                        />
                        <div className="space-y-1">
                            <Label htmlFor="recognize-future-caller">
                                Recognize this number on future calls
                            </Label>
                            <p className="text-muted-foreground text-xs">
                                Future calls from this number will be treated as
                                coming from the selected client or family and
                                may receive client-specific context.
                            </p>
                        </div>
                    </div>
                    {recognizeFutureCalls ? (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="caller-review-contact-name">
                                    Contact name
                                </Label>
                                <Input
                                    id="caller-review-contact-name"
                                    value={contactName}
                                    maxLength={255}
                                    disabled={isSubmitting}
                                    aria-invalid={contactNameMissing}
                                    onChange={(event) => {
                                        setContactName(event.target.value);
                                        setError(null);
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="caller-review-relation">
                                    Relationship (optional)
                                </Label>
                                <Input
                                    id="caller-review-relation"
                                    value={relation}
                                    maxLength={255}
                                    disabled={isSubmitting}
                                    onChange={(event) =>
                                        setRelation(event.target.value)
                                    }
                                />
                            </div>
                        </div>
                    ) : null}
                    {error ? (
                        <p role="alert" className="text-destructive text-sm">
                            {error}
                        </p>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        disabled={isSubmitting}
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} onClick={() => void submit()}>
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Link to client
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export function VoiceCallCallerLinkAction({
    activityEventId,
    callerPhone,
    initialContactName,
    organizationId,
    voiceCallId,
}: {
    readonly activityEventId?: string | null;
    readonly callerPhone: string;
    readonly initialContactName?: string | null;
    readonly organizationId: string;
    readonly voiceCallId: string;
}) {
    const invalidate = useInvalidateCallerReviewQueries(organizationId);
    const [linkOpen, setLinkOpen] = useState(false);
    const [dismissOpen, setDismissOpen] = useState(false);
    const [dismissReason, setDismissReason] = useState<
        'not_client_related' | 'spam_or_wrong_number' | 'unable_to_identify'
    >('not_client_related');
    const [isDismissing, setIsDismissing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function dismiss() {
        setIsDismissing(true);
        setError(null);
        try {
            await dismissVoiceCallCallerReview(organizationId, voiceCallId, {
                reason: dismissReason,
                note: null,
            });
            const successMessage = 'Review dismissed.';
            toast.success(successMessage, { persistNotification: false });
            setMessage(successMessage);
            setDismissOpen(false);
            await invalidate();
        } catch (caught) {
            setError(errorMessage(caught));
        } finally {
            setIsDismissing(false);
        }
    }

    return (
        <div className="border-border bg-muted/30 space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-sm font-medium">Unknown caller</p>
                    <p className="text-muted-foreground text-xs">
                        {callerPhone} is not linked to a client.
                    </p>
                </div>
                {!message ? (
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => setLinkOpen(true)}>
                            Link to client
                        </Button>
                        {activityEventId ? (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDismissOpen(true)}
                            >
                                Not client-related
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>
            {message ? <p className="text-xs font-medium">{message}</p> : null}
            {error ? (
                <p role="alert" className="text-destructive text-xs">
                    {error}
                </p>
            ) : null}
            <VoiceCallCallerReviewDialog
                callerPhone={callerPhone}
                initialContactName={initialContactName}
                onOpenChange={setLinkOpen}
                onResolved={(resolution) => {
                    setMessage(callerReviewResolutionMessage(resolution));
                }}
                open={linkOpen}
                organizationId={organizationId}
                voiceCallId={voiceCallId}
            />
            <Dialog open={dismissOpen} onOpenChange={setDismissOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Dismiss caller review?</DialogTitle>
                        <DialogDescription>
                            This closes the review without linking the call to a
                            client.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="caller-review-dismiss-reason">
                            Reason
                        </Label>
                        <select
                            id="caller-review-dismiss-reason"
                            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
                            value={dismissReason}
                            disabled={isDismissing}
                            onChange={(event) =>
                                setDismissReason(
                                    event.target.value as typeof dismissReason,
                                )
                            }
                        >
                            <option value="not_client_related">
                                Not client-related
                            </option>
                            <option value="spam_or_wrong_number">
                                Spam or wrong number
                            </option>
                            <option value="unable_to_identify">
                                Unable to identify
                            </option>
                        </select>
                    </div>
                    {error ? (
                        <p role="alert" className="text-destructive text-sm">
                            {error}
                        </p>
                    ) : null}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={isDismissing}
                            onClick={() => setDismissOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={isDismissing}
                            onClick={() => void dismiss()}
                        >
                            {isDismissing ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Dismiss caller review
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
